import { useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Stepper, Paper, Container } from "@mantine/core";
import { useForm } from "react-hook-form";
import { AuthContext } from "../../services/context";
import { getCurrentWorkspace } from "../../services/userConfig";
import {
  useValidateBatchUploadUserDocuments,
  useBatchUploadUserDocuments,
} from "../../api/user";
import { useGetTicketColumns } from "../../api/tickets";
import { toBase64 } from "../../services/helper";
import { ManifestStep } from "./BatchUpload/ManifestStep";
import { ValidationStep } from "./BatchUpload/ValidationStep";
import { ZIPUploadStep } from "./BatchUpload/ZIPUploadStep";
import { ResultsStep } from "./BatchUpload/ResultsStep";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { getFilenamesFromZip, matchManifestToZip } from "../../utils/zipUtils";

export default function BatchUploadWizard() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const currentWorkspace = getCurrentWorkspace(user);
  const eventId = currentWorkspace?.eventId;

  const { data: ticketColumns } = useGetTicketColumns({ eventId });

  const [active, setActive] = useState(0);
  const [validationResults, setValidationResults] = useState(null);
  const [uploadResults, setUploadResults] = useState(null);
  const [manifestHeaders, setManifestHeaders] = useState([]);
  const [shouldAutoCreateAnnouncement, setAutoCreateAnnouncement] =
    useState(false);
  const [shouldEmailRecipients, setShouldEmailRecipients] = useState(false);
  const [zipInspection, setZipInspection] = useState(null);

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      manifestFile: null,
      zipFile: null,
      identifierColumn: "",
      filenameColumn: "",
      documentName: "",
    },
  });

  const manifestFile = watch("manifestFile");

  const validateMutation = useValidateBatchUploadUserDocuments(eventId);
  const uploadMutation = useBatchUploadUserDocuments(eventId);

  const identifierOptions = useMemo(() => {
    const options = [
      { value: "email", label: "Email" },
      { value: "ticketid", label: "Ticket Id" },
    ];

    if (ticketColumns) {
      const defaultTicketColumns = [
        "firstName",
        "lastName",
        "email",
        "status",
        "category",
        "checkedInAt",
        "picture",
        "id",
        "ticketid",
      ];
      ticketColumns.forEach((col) => {
        const normName = col.name.toLowerCase().replace(/\s/g, "");
        if (
          defaultTicketColumns.includes(col.name) ||
          normName === "ticketid" ||
          normName === "email"
        )
          return;
        options.push({
          value: normName,
          label: col.name,
        });
      });
    }

    return options;
  }, [ticketColumns]);

  const autoDetectColumns = (headers) => {
    const normHeaders = headers.map((h) => h.toLowerCase().replace(/\s/g, ""));

    // Auto-detect identifier column
    const idOption = identifierOptions.find((opt) =>
      normHeaders.includes(opt.value)
    );
    if (idOption) {
      setValue("identifierColumn", idOption.value);
    }

    // Auto-detect filename column
    const fileIdx = normHeaders.findIndex(
      (h) =>
        h.includes("file") || h.includes("document") || h.includes("filename")
    );
    if (fileIdx !== -1) {
      setValue("filenameColumn", headers[fileIdx]);
    }
  };

  const handleManifestDrop = async (files) => {
    const file = files[0];
    setValue("manifestFile", file);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });

      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      const headers = rawData[0] || [];

      const cleanHeaders = headers
        .filter((h) => h != null)
        .map((h) =>
          String(h)
            .trim()
            .replace(/^["']|["']$/g, "")
        );

      setManifestHeaders(cleanHeaders);
      autoDetectColumns(cleanHeaders);
    } catch (error) {
      console.error("Error reading manifest file:", error);
    }
  };

  const handleStepDetails = handleSubmit(async (data) => {
    const base64file = await toBase64(data.manifestFile);

    const body = {
      file: {
        name: data.manifestFile.name,
        type: data.manifestFile.type || "text/csv",
        data: base64file,
      },
      identifierColumn: data.identifierColumn,
      filenameColumn: data.filenameColumn,
    };

    validateMutation.mutate(body, {
      onSuccess: (res) => {
        setValidationResults(res);
        setActive(1);
      },
    });
  });

  const handleZipDrop = async (files) => {
    const file = files[0];
    setValue("zipFile", file);
    setZipInspection(null);

    try {
      const zip = await JSZip.loadAsync(file);
      const zipFilenames = getFilenamesFromZip(zip);

      const manifestFilenames = (validationResults?.mapped || [])
        .map((r) => r.filename)
        .filter(Boolean);

      setZipInspection(matchManifestToZip(manifestFilenames, zipFilenames));
    } catch (error) {
      console.error("Error reading ZIP file:", error);
    }
  };

  const handleUpload = async () => {
    const currentZipFile = watch("zipFile");
    const base64zip = await toBase64(currentZipFile);

    const body = {
      file: {
        name: currentZipFile.name,
        type: currentZipFile.type || "application/zip",
        data: base64zip,
      },
      manifest: validationResults.mapped,
      documentName: watch("documentName"),
      shouldCreateAnnouncement: shouldAutoCreateAnnouncement,
      shouldEmailRecipients,
    };

    uploadMutation.mutate(body, {
      onSuccess: (res) => {
        setUploadResults(res);
        setActive(3);
      },
    });
  };

  const validationRecords = useMemo(
    () => [
      ...(validationResults?.unmapped || []),
      ...(validationResults?.mapped || []),
    ],
    [validationResults]
  );

  const nextStep = () =>
    setActive((current) => (current < 3 ? current + 1 : current));
  const prevStep = () =>
    setActive((current) => (current > 0 ? current - 1 : current));

  const renderStep = () => {
    switch (active) {
      case 0:
        return (
          <ManifestStep
            manifestFile={manifestFile}
            onManifestDrop={handleManifestDrop}
            onRemoveManifest={() => setValue("manifestFile", null)}
            identifierOptions={identifierOptions}
            manifestHeaders={manifestHeaders}
            identifierColumn={watch("identifierColumn")}
            onIdentifierColumnChange={(v) => setValue("identifierColumn", v)}
            filenameColumn={watch("filenameColumn")}
            onFilenameColumnChange={(v) => setValue("filenameColumn", v)}
            documentName={watch("documentName")}
            onDocumentNameChange={(v) => setValue("documentName", v)}
            onValidate={handleStepDetails}
            isPending={validateMutation.isPending}
            errors={errors}
          />
        );
      case 1:
        return (
          <ValidationStep
            validationResults={validationResults}
            validationRecords={validationRecords}
            identifierColumn={watch("identifierColumn")}
            filenameColumn={watch("filenameColumn")}
            onPrev={prevStep}
            onNext={nextStep}
          />
        );
      case 2:
        return (
          <ZIPUploadStep
            zipFile={watch("zipFile")}
            onZipDrop={handleZipDrop}
            onRemoveZip={() => {
              setValue("zipFile", null);
              setZipInspection(null);
            }}
            onUpload={handleUpload}
            onPrev={prevStep}
            isPending={uploadMutation.isPending}
            shouldAutoCreateAnnouncement={shouldAutoCreateAnnouncement}
            onToggleShouldAutoCreateAnnouncement={setAutoCreateAnnouncement}
            shouldEmailRecipients={shouldEmailRecipients}
            onToggleShouldEmailRecipients={setShouldEmailRecipients}
            zipInspection={zipInspection}
          />
        );
      case 3:
        return (
          <ResultsStep
            uploadResults={uploadResults}
            onFinish={() => navigate("/app/documents")}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Container size="md" py="xl">
      <Paper withBorder p="xl" radius="md" style={{ minHeight: 400 }}>
        <Stepper
          active={active}
          onStepClick={setActive}
          allowNextStepsSelect={false}
          mb="lg"
        >
          <Stepper.Step label="Manifest" description="Upload CSV/Excel" />
          <Stepper.Step label="Validation" description="Review matches" />
          <Stepper.Step label="Documents" description="Upload ZIP" />
          <Stepper.Step label="Results" description="Final summary" />
        </Stepper>

        <Paper mt="xl">{renderStep()}</Paper>
      </Paper>
    </Container>
  );
}
